<template>
  <div 
    class="h-full bg-gray-100" 
    :style="{ 
      backgroundColor: (!loading && (isHomePage) && tourSettings?.start_background_color) ? tourSettings.start_background_color : '', 
      minHeight: 'calc(var(--vh) * 100)' 
    }"
  >
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mx-auto"></div>
        <p class="mt-4">{{ $t('loading', 'Loading...') }}</p>
      </div>
    </div>

    <!-- Show NotFound component when there's a not_found error -->
    <NotFound v-else-if="shouldShowNotFound" />

    <div v-else-if="tourSettings" class="transition-opacity duration-500 ease-in-out opacity-100">
      <!-- Menu -->
      <Menu v-if="isMenuOpen" :toggleMenu="toggleMenu" />

      <!-- Homepage background -->
      <div v-if="isHomePage" class="absolute top-0 z-10 w-full">
        <div
          :style="{
            backgroundImage: tourSettings?.main_picture?.preview_url ? `url(${tourSettings.main_picture.preview_url})` : (backgroundImage ? `url(${backgroundImage})` : 'none'),
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            height: tourSettings.start_background_color ? '50vh' : '100vh',
          }"
          class="w-full"
        >
          <div
            v-if="tourSettings.start_background_color"
            class="absolute bottom-0 left-0 w-full h-1/2 pointer-events-none mb-[-1px]"
            :style="{
              background: `linear-gradient(to bottom, transparent, ${tourSettings.start_background_color})`
            }"
          />
        </div>
      </div>

      <!-- Main content -->
      <div class="relative w-full z-20">
        <main class="w-full" style="min-height: calc(var(--vh) * 100)">
          <div class="flex flex-col h-full" style="min-height: calc(var(--vh) * 100)">
            <!-- Header -->
            <Header
              v-if="!isHomePage && activeTour"
              :isMenuOpen="isMenuOpen"
              :toggleMenu="toggleMenu"
            />

            <!-- Router view -->
            <router-view :key="$route.name + '|' + ($route.params.tourId || '') + '|' + ($route.query.lang || '')" />
          </div>

          <!-- Modal -->
          <Modal />

          <!-- Overlay -->
          <Overlay />
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import Header from './Header.vue'
import Menu from './Menu.vue'
import Modal from './Modal.vue'
import Overlay from './Overlay.vue'
import NotFound from '../views/NotFound.vue'

export default {
  name: 'VueLayout',
  components: {
    Header,
    Menu,
    Modal,
    Overlay,
    NotFound
  },
  setup() {
    const route = useRoute()
    const loading = ref(true)
    const isMenuOpen = ref(false)
    const backgroundImage = ref(null)

    return {
      loading,
      isMenuOpen,
      backgroundImage,
      route
    }
  },
  computed: {
    ...mapGetters('tours', ['activeTour']),
    ...mapGetters('settings', ['tourSettings', 'error']),
    
    isHomePage() {
      return this.route.name === 'home' || this.route.name === 'root-home'
    },
    
    isPointPage() {
      return this.route.path.includes('/point/')
    },

    shouldShowNotFound() {
      return this.error && this.error.type === 'not_found'
    }
  },
  methods: {
    ...mapActions('settings', ['loadTourSettings']),
    ...mapActions('tours', ['setActiveTour']),
    normalizeLanguage(desiredLang) {
      console.log("normalizeLanguage", desiredLang)
      const settings = this.$store.getters['settings/tourSettings']
      const available = Array.isArray(settings?.languages) ? settings.languages.map(l => l.code) : []
      const current = this.$i18n?.locale
      console.log("normalizeLanguage", settings, available, current)
      // If no languages configured (account branding), just apply desired if present
      if (!available.length) {
        if (desiredLang && current !== desiredLang) {
          this.$i18n.locale = desiredLang
        }
        return
      }
      const candidate = desiredLang || current
      const supported = available.includes(candidate) ? candidate : available[0]
      if (this.$i18n.locale !== supported) {
        this.$i18n.locale = supported
      }
      // Ensure URL has supported lang
      const route = this.$route
      if (route.query?.lang !== supported) {
        this.$router.replace({ name: route.name, params: route.params, query: { ...route.query, lang: supported } })
      }
    },
    
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
      if (!this.isMenuOpen) {
        document.documentElement.classList.add("overflow-hidden")
        document.body.classList.add("overflow-hidden")
      } else {
        document.documentElement.classList.remove("overflow-hidden")
        document.body.classList.remove("overflow-hidden")
      }
    },
    
    // Check if we should clear activeTour based on domain
    // checkDomainAndClearTour() {
    //   if (!this.tourSettings?.domains || !Array.isArray(this.tourSettings.domains)) {
    //     return
    //   }
    //
    //   const currentDomain = window.location.hostname
    //   const accountDomain = this.tourSettings.domains[0] // First domain is the account domain
    //   const currentPath = window.location.pathname
    //
    //   console.log('Domain check for tour clearing:', {
    //     currentDomain,
    //     accountDomain,
    //     activeTour: this.activeTour,
    //     currentPath,
    //     isOnAccountDomain: currentDomain === accountDomain,
    //     isOnSpecificUrl: currentPath.includes('/point/') || currentPath.includes('/qr/'),
    //     shouldClear: currentDomain === accountDomain && this.activeTour && !currentPath.includes('/point/') && !currentPath.includes('/qr/')
    //   })
    //
    //   // Only clear activeTour if we're on the account domain, have an active tour,
    //   // and we're NOT on a specific point or QR code URL (to allow tour selection)
    //   if (currentDomain === accountDomain && this.activeTour &&
    //       !currentPath.includes('/point/') && !currentPath.includes('/qr/')) {
    //     console.log('On account domain with active tour (not on specific URL), clearing tour')
    //     this.setActiveTour(null)
    //     localStorage.removeItem('activeTour')
    //   }
    // },
    
    async loadSettings() {
      try {
        console.log('=== LAYOUT LOAD SETTINGS ===')
        console.log('Route params:', this.route.params)
        console.log('Active tour (ignored for settings):', this.activeTour)
        console.log('Current URL:', window.location.pathname)
        console.log('Route path:', this.route.path)
        console.log('Route name:', this.route.name)
        
        const tourId = this.route.params.tourId || null
        console.log('Loading settings for tourId from params:', tourId)
        const data = await this.$store.dispatch('settings/loadTourSettings', tourId)
        
        console.log('Settings loaded, data:', data)
        
        // Check for error state - don't redirect, let template handle it
        if (this.error && this.error.type === 'not_found') {
          console.log('Tour not found, showing error state')
          this.loading = false
          return
        }
        
        if (data) {
          console.log('Settings data received, updating UI...')
          // Set background image
          if (data.main_picture) {
            this.backgroundImage = data.main_picture.preview_url
          }
          
          // Set active tour if available
          if (data.id) {
            console.log('Setting active tour to:', data.id)
            this.setActiveTour(data.id)
          }
          
          // Check if we should clear activeTour based on domain
          //this.checkDomainAndClearTour()
          
          this.loading = false
        } else {
          console.log('No settings data received')
          this.loading = false
        }
      } catch (error) {
        console.error('Error loading settings:', error)
        this.loading = false
        
        // Don't redirect on 404, let error state handle it
        if (error.response && error.response.status === 404) {
          console.log('404 error, showing error state instead of redirecting')
        }
      }
    },
    refreshPage() {
      // Clear error state and reload settings
      this.$store.commit('settings/SET_ERROR', null)
      this.loadSettings()
    }
  },
  watch: {
    // Sync i18n and URL lang and validate against tour languages
    '$route.query.lang': {
      handler(newLang) {
        this.normalizeLanguage(newLang)
      },
      immediate: true
    },
    // Note: runtime settings loads are handled by router guards and initial mount.
    // Intentionally removed reactive reloads on route/activeTour to prevent races during navigation.
    
    tourSettings: {
      handler(newSettings, oldSettings) {
        console.log('=== LAYOUT TOUR SETTINGS CHANGED ===')
        console.log('New settings ID:', newSettings?.id)
        console.log('Old settings ID:', oldSettings?.id)
        console.log('Settings ID changed:', newSettings?.id !== oldSettings?.id)
        console.log('Active tour:', this.activeTour)
        console.log('Settings match active tour:', newSettings?.id === this.activeTour)
        // Validate/normalize language for this tour
        this.normalizeLanguage(this.$route.query?.lang)
        
        // Only process if we have new settings and they're different from old settings
        if (newSettings && (!oldSettings || JSON.stringify(newSettings) !== JSON.stringify(oldSettings))) {
          this.loading = false
          // Check if we should clear activeTour based on domain
          //this.checkDomainAndClearTour()
        }
      },
      immediate: false // Changed from true to false to prevent immediate triggering
    },
    
    // Watch for error state changes to update URL
    error: {
      handler(newError, oldError) {
        // Only process if error actually changed
        if (newError && newError !== oldError && newError.type === 'not_found') {
          // Redirect to home URL to start fresh
          const currentPath = window.location.pathname
          if (currentPath !== '/') {
            this.$router.replace('/')
          }
        }
      },
      immediate: false // Changed from true to false to prevent immediate triggering
    }
  },
  async mounted() {
    console.log('=== LAYOUT MOUNTED ===')
    console.log('Route params on mount:', this.route.params)
    console.log('Active tour on mount:', this.activeTour)
    console.log('Tour settings on mount:', this.tourSettings)
    
    // Check if we need to load settings
    const shouldLoadSettings = (
      // Root-home: no tourId and no settings yet -> load general branding
      (!this.route.params.tourId && !this.tourSettings) ||
      // Tour home: tourId present and either no settings or mismatch -> load tour settings
      (!!this.route.params.tourId && (
        !this.tourSettings || (this.activeTour && this.tourSettings.id !== this.activeTour)
      ))
     // or when the toursettings are loaded but not in the route params anymore
     || (!this.route.params.tourId && this.tourSettings && this.tourSettings.id !== this.activeTour)
    )
    
    console.log('Should load settings:', shouldLoadSettings)
    console.log('Current settings tour ID:', this.tourSettings?.id)
    console.log('Active tour ID:', this.activeTour)
    console.log('Settings match active tour:', this.tourSettings?.id === this.activeTour)
    
    if (shouldLoadSettings) {
      console.log('Loading settings (no settings or tour mismatch)...')
      await this.loadSettings()
    } else {
      console.log('Tour settings already exist and match active tour, skipping load')
      this.loading = false
    }
  }
}
</script> 